import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, X, Eye, Trash2, User, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';
import AddAdmin from '../components/modals/AddAdmin';
import { selectAdminList, setAdminStatus, deleteAdmin } from '../redux/adminSlice';
import { getCountries } from '../redux/masterSlice';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../components/CommonLoader';
import MasterLabel from '../components/common/MasterLabel';
import ErrorPage from '../components/ErrorPage';
import { getFileURL } from '../utils/fileURL';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterSearchableList, FilterPopover } from '../components/common/FilterPanel';
import { getTenantOptions } from '../redux/tenantSlice';
import useCanManage from '../hooks/useCanManage';
import { canAccess } from '../utils/platformPermissions';

const MySwal = withReactContent(Swal);

export default function Admin() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(['adminpage']);
    const { adminList, adminTotal, adminListLoading, mutating, error } = useSelector((state) => state.admin);
    const { countries } = useSelector((state) => state.master);
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('tenants.manage');
    // The platform side sees admins from every tenant, so the list needs to say
    // which hospital each one belongs to — and be filterable by it. Keyed on the
    // permission, not the isSuperAdmin flag: platform staff holding tenants.view
    // (what opens this page) get the same column and filter.
    const showTenant = canAccess('tenants.view');
    const tenantOptions = useSelector((state) => state.tenant?.tenantOptions);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('adminActiveTab') || 'active'); // 'active' | 'inactive'
    // Bumped after an admin is created to force a list refetch even when the tab/page don't change
    const [refreshKey, setRefreshKey] = useState(0);

    // Persist active tab to session storage
    useEffect(() => {
        sessionStorage.setItem('adminActiveTab', activeTab);
    }, [activeTab]);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({ gender: [], countryId: [], tenantId: [] });
    const [filters, setFilters] = useState({ gender: [], countryId: [], tenantId: [] });

    const searchTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    // Human-readable gender labels (with English fallbacks) for chips/tables.
    const genderLabel = (g) => {
        const map = { male: 'Male', female: 'Female', other: 'Other' };
        return t(`adminpage.${g}`, map[g] || g);
    };

    // Validate search input
    const validateSearchInput = (value) => {
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > 50) {
            return t('adminpage.search_too_long', 'Search term cannot exceed 50 characters');
        }
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('adminpage.search_spaces_only', 'Search term cannot contain only spaces');
        }
        return '';
    };

    // Debounce search query (only if no error)
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        if (!searchError) {
            searchTimeoutRef.current = setTimeout(() => {
                const trimmedSearch = searchQuery.trim();
                setDebouncedSearchQuery(trimmedSearch);
                setCurrentPage(1);
            }, 500);
        }
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, searchError]);

    // Fetch countries on mount
    useEffect(() => {
        dispatch(getCountries());
        if (showTenant) dispatch(getTenantOptions());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
        }
    }, [isFilterOpen]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Refocus search input after loading
    useEffect(() => {
        const active = document.activeElement === inputRef.current;
        if (!active && inputRef.current && searchQuery.length > 0) {
            inputRef.current.focus();
        }
    }, [adminListLoading]);

    // Fetch admins
    useEffect(() => {
        if (searchError) {
            return;
        }
        dispatch(selectAdminList({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab, // 'active' | 'inactive'
            gender: filters.gender,
            countryId: filters.countryId,
            tenantId: filters.tenantId?.length ? filters.tenantId.join(',') : undefined,
        }));
    }, [currentPage, itemsPerPage, debouncedSearchQuery, activeTab, filters, dispatch, searchError, refreshKey]);

    const refetch = () => {
        dispatch(selectAdminList({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab,
            gender: filters.gender,
            countryId: filters.countryId,
            tenantId: filters.tenantId?.length ? filters.tenantId.join(',') : undefined,
        }));
    };

    const handleCreated = () => {
        // A newly created admin is active by default, so land on the Active tab.
        if (activeTab !== 'active') {
            setActiveTab('active');
        }
        setCurrentPage(1);
        // Force a refetch even when we're already on the Active tab / page 1.
        setRefreshKey((k) => k + 1);
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;
        setSearchQuery(finalValue);
        const err = validateSearchInput(finalValue);
        setSearchError(err);
        setCurrentPage(1);
    };

    const handleDeleteAdmin = async (adminId) => {
        const result = await MySwal.fire({
            title: t('adminpage.delete_confirm_title', 'Delete admin?'),
            text: t('adminpage.delete_confirm_text', 'This action cannot be undone.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('adminpage.delete_confirm_button', 'Delete'),
            cancelButtonText: t('adminpage.cancel', 'Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const response = await dispatch(deleteAdmin(adminId)).unwrap();
                    return response;
                } catch (err) {
                    toast.warn(typeof err?.error === 'string' ? err.error : t('adminpage.delete_error_text', 'Failed to delete admin'));
                    throw err;
                }
            },
            allowOutsideClick: () => !MySwal.isLoading(),
        });

        if (result.isConfirmed) {
            toast.success(t('adminpage.delete_success_text', 'Admin deleted successfully'));
            refetch();
        }
    };

    const handleStatusToggle = async (admin) => {
        const isActive = admin.status === 'active';
        const newStatus = isActive ? 'inactive' : 'active';
        const actionText = isActive ? t('adminpage.deactivate', 'deactivate') : t('adminpage.activate', 'activate');

        const result = await MySwal.fire({
            title: t('adminpage.status_toggle_confirm_title', 'Confirm Status Change'),
            text: t('adminpage.status_toggle_confirm_text', `Are you sure you want to ${actionText} this admin?`),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('adminpage.confirm', 'Confirm'),
            cancelButtonText: t('adminpage.cancel', 'Cancel'),
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await dispatch(setAdminStatus({ adminId: admin.adminId, status: newStatus })).unwrap();
            refetch();
            toast.success(newStatus === 'active'
                ? t('adminpage.status_activated', 'Admin activated successfully')
                : t('adminpage.status_deactivated', 'Admin deactivated successfully'));
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('adminpage.status_update_error_text', 'Failed to update admin status'));
        }
    };

    // Always map admins to include fullName property
    const adminsWithFullName = (adminList || []).map((admin) => ({
        ...admin,
        fullName: `${admin.name || ''} ${admin.surname || ''}`.trim()
    }));

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const currentAdmins = adminsWithFullName;
    const totalItems = adminTotal;
    const indexOfFirstAdmin = (currentPage - 1) * itemsPerPage;

    // Handle error state
    if (error) {
        return <ErrorPage error={error} pageName={t('adminpage.all_admins', 'Admins Page')} />;
    }

    return (
        <div data-testid="admin-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Header Row */}
            <div data-testid="admin-page-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="admin-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                    {t('adminpage.all_admins', 'Admins')}
                </h1>
                {canManage && (
                <button
                    data-testid="add-admin-button"
                    onClick={() => setIsAddAdminOpen(true)}
                    disabled={adminListLoading}
                    className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${adminListLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <Plus data-testid="add-admin-icon" className="w-5 h-5" />
                    <span data-testid="add-admin-text">{t('adminpage.add_admin', 'Add Admin')}</span>
                </button>
                )}
            </div>

            {/* Search + Filter Row */}
            <div data-testid="search-container" className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    <button
                        data-testid="admin-tab-active"
                        onClick={() => { setActiveTab('active'); setCurrentPage(1); }}
                        className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'active'
                            ? 'text-white'
                            : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                            }`}
                    >
                        {activeTab === 'active' && (
                            <motion.div
                                layoutId="activeAdminStatusTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{t('adminpage.active', 'Active')}</span>
                    </button>

                    <button
                        data-testid="admin-tab-inactive"
                        onClick={() => { setActiveTab('inactive'); setCurrentPage(1); }}
                        className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'inactive'
                            ? 'text-white'
                            : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                            }`}
                    >
                        {activeTab === 'inactive' && (
                            <motion.div
                                layoutId="activeAdminStatusTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{t('adminpage.inactive', 'Inactive')}</span>
                    </button>
                </div>
                <div className="relative w-full sm:w-80">
                    <input
                        ref={inputRef}
                        data-testid="search-input"
                        type="text"
                        placeholder={t('adminpage.search_placeholder', 'Search name / email / id…')}
                        value={searchQuery}
                        onChange={handleSearch}
                        disabled={false}
                        maxLength={50}
                        className={`w-full p-2 pl-10 pr-8 border rounded-xl focus:outline-none focus:ring-2 ${searchError
                            ? 'border-red-500 focus:ring-red-500 bg-red-50'
                            : 'border-gray-300 focus:ring-[#4ABDBD]'
                            } ${adminListLoading ? "opacity-70" : ""}`}
                    />
                    <Search data-testid="search-icon" className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#4ABDBD]" />
                    {searchError && (
                        <p data-testid="search-error" className="text-red-600 text-xs mt-1 absolute -bottom-5 left-0">{searchError}</p>
                    )}
                </div>

                {/* Filter Button + Popup */}
                <div data-testid="admin-filter-container" className="relative w-full sm:w-auto">
                    <button
                        data-testid="filter-button"
                        ref={filterButtonRef}
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="group flex items-center justify-center gap-2 h-full px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                        aria-label={t('adminpage.filter', 'Filter')}
                        aria-expanded={isFilterOpen}
                    >
                        <SlidersHorizontal className="w-5 h-5" />
                    </button>

                    {isFilterOpen && (
                        <FilterPopover ref={filterRef}>
                            <FilterPanel
                                onClear={() => {
                                    setTempFilters({ gender: [], countryId: [] });
                                    setFilters({ gender: [], countryId: [] });
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                onApply={() => {
                                    setFilters(tempFilters);
                                    setCurrentPage(1);
                                    setIsFilterOpen(false);
                                }}
                                clearLabel={t('adminpage.clear', 'Clear')}
                                applyLabel={t('adminpage.apply', 'Apply')}
                            >
                                {/* Tenant first — it's the widest cut for a Super Admin
                                    looking at admins from every hospital. Searchable
                                    because the tenant list grows without bound. */}
                                {showTenant && (
                                    <FilterSection title={t('adminpage.tenant', 'Tenant')} isActive={tempFilters.tenantId.length > 0}>
                                        <FilterSearchableList
                                            multiple
                                            items={tenantOptions || []}
                                            value={(tempFilters.tenantId || []).map((id) => ({ tenantId: id }))}
                                            onChange={(val) => setTempFilters({
                                                ...tempFilters,
                                                tenantId: (val || []).map((v) => v.tenantId),
                                            })}
                                            labelKey="name"
                                            sublabelKey="__none__"
                                            searchKeys={['tenantId']}
                                            valueKey="tenantId"
                                            searchPlaceholder={t('adminpage.search_tenant', 'Search tenant...')}
                                            noResultsText={t('adminpage.no_tenants', 'No tenants found')}
                                            allLabel={t('adminpage.all_tenants', 'All tenants')}
                                        />
                                    </FilterSection>
                                )}

                                <FilterSection title={t('adminpage.gender', 'Gender')} isActive={tempFilters.gender.length > 0}>
                                    <FilterOptionList
                                        multiple
                                        value={tempFilters.gender}
                                        onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                        options={[
                                            { value: 'male', label: t('adminpage.male', 'Male') },
                                            { value: 'female', label: t('adminpage.female', 'Female') },
                                            { value: 'Not_Communicated', label: t('common.gender.notCommunicated', 'Not Communicated') },
                                            { value: 'other', label: t('adminpage.other', 'Other') },
                                        ]}
                                    />
                                </FilterSection>

                                <FilterSection title={t('adminpage.country', 'Country')} isActive={tempFilters.countryId.length > 0}>
                                    <FilterCountryList
                                        multiple
                                        value={tempFilters.countryId}
                                        onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                        countries={countries}
                                        searchPlaceholder={t('adminpage.search_placeholder', 'Search...')}
                                    />
                                </FilterSection>
                            </FilterPanel>
                        </FilterPopover>
                    )}
                </div>
            </div>

            {/* ---- Applied Filters Display ---- */}
            {(filters.gender.length > 0 || filters.countryId.length > 0 || filters.tenantId.length > 0) && (
                <div data-testid="applied-filters-container" className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span data-testid="applied-filters-label" className="text-sm font-semibold text-gray-700 mr-2">
                            {t('adminpage.applied_filters', 'Applied Filters')}:
                        </span>

                        {filters.tenantId.map((tid) => (
                            <div key={`tenant-${tid}`} data-testid={`applied-filter-tenant-${tid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('adminpage.tenant', 'Tenant')}:</span> {(tenantOptions || []).find((x) => x.tenantId === tid)?.name || t('moc.hr_detail_hospital', 'Hospital')}
                                </span>
                                <button
                                    data-testid={`remove-tenant-filter-${tid}`}
                                    onClick={() => setFilters({ ...filters, tenantId: filters.tenantId.filter((x) => x !== tid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} data-testid={`applied-filter-gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('adminpage.gender', 'Gender')}:</span> {genderLabel(g)}
                                </span>
                                <button
                                    data-testid={`remove-gender-filter-${g}`}
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} data-testid={`applied-filter-country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('adminpage.country', 'Country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || '—'} />
                                </span>
                                <button
                                    data-testid={`remove-country-filter-${cid}`}
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}

                        <button
                            data-testid="clear-all-filters-button"
                            onClick={() => {
                                setFilters({ gender: [], countryId: [], tenantId: [] });
                                setTempFilters({ gender: [], countryId: [], tenantId: [] });
                                setCurrentPage(1);
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                        >
                            <X className="h-3.5 w-3.5" />
                            {t('adminpage.clear_all', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}

            {/* Table for larger screens, Cards for mobile */}
            {adminListLoading ? (
                <div data-testid="admin-loading-container" className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span data-testid="admin-loading-text" className="text-xl text-[#4ABDBD]">{t('adminpage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    {/* Table for sm and above */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="admins-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr data-testid="table-header" className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th data-testid="serial-no-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('adminpage.serial_no', 'S.No')}</th>
                                    <th data-testid="name-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('adminpage.full_name', 'Name')}</th>
                                    {showTenant && (
                                        <th data-testid="tenant-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('adminpage.tenant', 'Tenant')}</th>
                                    )}
                                    <th data-testid="email-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('adminpage.email', 'Email')}</th>
                                    <th data-testid="phone-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('adminpage.phone', 'Phone')}</th>
                                    <th data-testid="status-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('adminpage.status', 'Status')}</th>
                                    <th data-testid="action-header" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('adminpage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="admins-table-body">
                                {currentAdmins.length > 0 ? (
                                    currentAdmins.map((admin, index) => {
                                        const serialNumber = indexOfFirstAdmin + index + 1;
                                        const getInitials = () => {
                                            const nameInitial = admin.name ? admin.name.charAt(0).toUpperCase() : '';
                                            const surnameInitial = admin.surname ? admin.surname.charAt(0).toUpperCase() : '';
                                            return nameInitial + surnameInitial;
                                        };
                                        const initials = getInitials();
                                        const isActive = admin.status === 'active';
                                        return (
                                            <tr key={admin.adminId} data-testid={`admin-row-${admin.adminId}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                                <td data-testid={`admin-serial-${admin.adminId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{serialNumber}</td>
                                                <td data-testid={`admin-name-${admin.adminId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                                                    <div className="flex items-center gap-3">
                                                        <div className="relative w-10 h-10 flex-shrink-0">
                                                            {admin.image ? (
                                                                <img
                                                                    src={getFileURL(admin.image)}
                                                                    alt={admin.fullName || 'Admin'}
                                                                    className="w-10 h-10 rounded-full object-cover shadow-md"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                        if (fallback) fallback.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div
                                                                className={`image-fallback ${admin.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full bg-[#4ABDBD] items-center justify-center shadow-md`}
                                                            >
                                                                {initials ? (
                                                                    <span className="text-white text-sm font-bold uppercase">
                                                                        {initials}
                                                                    </span>
                                                                ) : (
                                                                    <User className="w-5 h-5 text-white" />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span>{admin.fullName || '-'}</span>
                                                    </div>
                                                </td>
                                                {showTenant && (
                                                    <td data-testid={`admin-tenant-${admin.adminId}`} className="p-3 text-xs">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold">
                                                            {admin.tenantName || (admin.tenantId ? t('moc.hr_detail_hospital', 'Hospital') : '-')}
                                                        </span>
                                                    </td>
                                                )}
                                                <td data-testid={`admin-email-${admin.adminId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{admin.email || '-'}</td>
                                                <td data-testid={`admin-phone-${admin.adminId}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{admin.phone || '-'}</td>
                                                <td data-testid={`admin-status-${admin.adminId}`} className="p-3 text-xs">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            role="switch"
                                                            aria-checked={isActive}
                                                            data-testid={`status-toggle-${admin.adminId}`}
                                                            onClick={() => canManage && !mutating && handleStatusToggle(admin)}
                                                            disabled={mutating || !canManage}
                                                            title={isActive ? t('adminpage.deactivate', 'Deactivate') : t('adminpage.activate', 'Activate')}
                                                            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300 ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'} ${mutating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        >
                                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                                        </button>
                                                        <span className={`text-xs font-semibold ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                                            {isActive ? t('adminpage.active', 'Active') : t('adminpage.inactive', 'Inactive')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td data-testid={`admin-actions-${admin.adminId}`} className="p-3 text-xs flex justify-center gap-2">
                                                    <button
                                                        data-testid={`view-admin-${admin.adminId}`}
                                                        onClick={() => navigate('/manageAdminProfile', { state: { key: admin.adminId, returnTab: activeTab } })}
                                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                        <Eye data-testid={`view-icon-${admin.adminId}`} className="h-4 w-4 relative z-10" />
                                                    </button>
                                                    {canManage && (
                                                    <button
                                                        data-testid={`delete-admin-${admin.adminId}`}
                                                        onClick={() => handleDeleteAdmin(admin.adminId)}
                                                        disabled={mutating || !canManage}
                                                        title={t('adminpage.delete', 'Delete')}
                                                        className={`group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden ${mutating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                        <Trash2 data-testid={`delete-icon-${admin.adminId}`} className="h-4 w-4 relative z-10" />
                                                    </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr data-testid="no-admins-row-desktop">
                                        <td data-testid="no-admins-message" {...{ colSpan: showTenant ? 7 : 6 }} className="p-3 text-center text-gray-500">
                                            {t('adminpage.no_admins_found', 'No admins found.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Card layout for mobile screens */}
                    <div className="block sm:hidden space-y-4">
                        {currentAdmins.length > 0 ? (
                            currentAdmins.map((admin, index) => {
                                const serialNumber = indexOfFirstAdmin + index + 1;
                                const getInitials = () => {
                                    const nameInitial = admin.name ? admin.name.charAt(0).toUpperCase() : '';
                                    const surnameInitial = admin.surname ? admin.surname.charAt(0).toUpperCase() : '';
                                    return nameInitial + surnameInitial;
                                };
                                const initials = getInitials();
                                const isActive = admin.status === 'active';
                                return (
                                    <div
                                        key={admin.adminId}
                                        data-testid={`admin-card-${admin.adminId}`}
                                        className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                                    >
                                        <div className="flex justify-between items-center mb-2">
                                            <span data-testid={`admin-serial-${admin.adminId}`} className="text-xs font-bold text-[#4ABDBD]">
                                                {t('adminpage.serial_no', 'S.No')}: {serialNumber}
                                            </span>
                                            <div className="flex gap-2">
                                                <button
                                                    data-testid={`view-admin-mobile-${admin.adminId}`}
                                                    onClick={() => navigate('/manageAdminProfile', { state: { key: admin.adminId, returnTab: activeTab } })}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Eye className="h-4 w-4 relative z-10" />
                                                </button>
                                                {canManage && (
                                                <button
                                                    data-testid={`delete-admin-mobile-${admin.adminId}`}
                                                    onClick={() => handleDeleteAdmin(admin.adminId)}
                                                    disabled={mutating || !canManage}
                                                    title={t('adminpage.delete', 'Delete')}
                                                    className={`group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden ${mutating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Trash2 className="h-4 w-4 relative z-10" />
                                                </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div data-testid={`admin-name-${admin.adminId}`} className="flex items-center gap-3 text-sm font-semibold text-gray-800">
                                                <div className="relative w-10 h-10 flex-shrink-0">
                                                    {admin.image ? (
                                                        <img
                                                            src={getFileURL(admin.image)}
                                                            alt={admin.fullName || 'Admin'}
                                                            className="w-10 h-10 rounded-full object-cover shadow-md"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                                if (fallback) fallback.style.display = 'flex';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div
                                                        className={`image-fallback ${admin.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full bg-[#4ABDBD] items-center justify-center shadow-md`}
                                                    >
                                                        {initials ? (
                                                            <span className="text-white text-sm font-bold uppercase">
                                                                {initials}
                                                            </span>
                                                        ) : (
                                                            <User className="w-5 h-5 text-white" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="font-bold">{t('adminpage.full_name', 'Name')}:</span> {admin.fullName || '-'}
                                                </div>
                                            </div>
                                            <div data-testid={`admin-email-${admin.adminId}`} className="text-xs text-gray-600">
                                                <span className="font-bold">{t('adminpage.email', 'Email')}:</span> {admin.email || '-'}
                                            </div>
                                            <div data-testid={`admin-phone-${admin.adminId}`} className="text-xs text-gray-600">
                                                <span className="font-bold">{t('adminpage.phone', 'Phone')}:</span> {admin.phone || '-'}
                                            </div>
                                            <div data-testid={`admin-status-${admin.adminId}`} className="text-xs flex items-center gap-2">
                                                <span className="font-bold">{t('adminpage.status', 'Status')}:</span>
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={isActive}
                                                    data-testid={`status-toggle-mobile-${admin.adminId}`}
                                                    onClick={() => canManage && !mutating && handleStatusToggle(admin)}
                                                    disabled={mutating || !canManage}
                                                    title={isActive ? t('adminpage.deactivate', 'Deactivate') : t('adminpage.activate', 'Activate')}
                                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300 ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'} ${mutating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                                </button>
                                                <span className={`text-xs font-semibold ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                                    {isActive ? t('adminpage.active', 'Active') : t('adminpage.inactive', 'Inactive')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div data-testid="no-admins-row-mobile" className="p-3 text-center text-gray-500">
                                {t('adminpage.no_admins_found', 'No admins found.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Pagination */}
            {!adminListLoading && totalItems > 0 && (
                <Pagination
                    totalItems={totalItems}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    custombutton={currentAdmins.length === 0 ? 'disable' : ''}
                />
            )}

            {/* Add Admin Modal */}
            <AddAdmin
                isOpen={isAddAdminOpen}
                onClose={() => setIsAddAdminOpen(false)}
                onCreated={handleCreated}
            />
        </div>
    );
}
